import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
	AlreadyCancelledError,
	cancelSubscription,
	getSubscription,
	payCancellationFee,
	type CancellationType,
	type FetchSubscriptionParams,
	type Phase,
	type PhaseType,
	type RxStatus,
	type Subscription,
	type SubscriptionStatus,
} from '@/api/medBilling'
import { useSurveyStore } from '@/store/survey'

// Ported from med-web-funnel `src/store/subscription.js`. Kept almost as-is on purpose:
// the funnel is the source of truth for subscription shape and predicates. Removed here
// (and intentionally not re-implemented):
//   - DOWNGRADE_PRODUCTS_MAP / findDowngradeProduct / isDowngradedProduct
//   - canDowngrade getter
//   - checkStatus.isDowngraded / isPendingDowngrade
//   - scheduled-downgrade special case in setSubscription
// If downgrade UX comes back to this app, port these together with the funnel's current map.
//
// Setup-style store (vs. funnel's options-style) chosen for cleaner TS inference when
// getters call other getters — see https://pinia.vuejs.org/core-concepts/#Setup-Stores.

type PhaseMap = Partial<Record<PhaseType, Phase>>

type MergedSubscription = Subscription & { currency?: string }

/**
 * The subscription statuses an Rx state is meaningful for: `awaiting_approval` (the review is
 * running) and `active` (it finished and the plan is live). Every other status is terminal or
 * interrupted — `cancelled` / `declined` / `paused` / `expired` / `past_due` — and there a leftover
 * order state only misleads: a cancelled plan showing "IN REVIEW" reads as if a clinician were
 * still working on it. The union is open (`| string`), so an unknown future status is treated like
 * the terminal ones and hides the row; add it here if it should show one.
 */
const RX_STATUS_STATUSES: readonly SubscriptionStatus[] = ['active', 'awaiting_approval']

export const useSubscriptionStore = defineStore('subs', () => {
	const subscription = ref<Subscription | null>(null)
	const loading = ref(false)
	const error = ref<string | null>(null)
	/**
	 * `true` once the first `fetchSubscription()` settled (success, empty or error). The router
	 * starts its first navigation at `app.use(router)` — before main.ts fetches — so a guard that
	 * reads the subscription (the commitment-route guard) waits on this instead of seeing `null`.
	 */
	const bootstrapped = ref(false)

	const hasSubscription = computed(() => Boolean(subscription.value && Object.keys(subscription.value).length))

	const getPhases = computed<PhaseMap>(() => {
		const phases = subscription.value?.phases ?? []
		return phases.reduce<PhaseMap>((map, phase) => {
			const iterationPrices = phase.iteration_prices ?? []
			const iterationPrice = iterationPrices.find(
				(price) => phase.is_active && price.iteration === subscription.value?.billing_period_number,
			)
			map[phase.phase_type] = {
				...phase,
				price: iterationPrice?.price ?? phase.price,
				discount: iterationPrice?.discount ?? null,
			} as Phase
			return map
		}, {})
	})

	const getSubscriptionView = computed<MergedSubscription | Record<string, never>>(() => {
		const phases = getPhases.value
		const sub = subscription.value
		if (!hasSubscription.value || !sub) return {}
		return {
			...(phases.regular?.currency ? { currency: phases.regular.currency } : {}),
			...sub,
			...phases,
		}
	})

	const getCurrency = computed(() => {
		const sub = getSubscriptionView.value as MergedSubscription
		return sub.currency?.toLowerCase?.()
	})

	const getOriginalProductId = computed(() => {
		const sub = getSubscriptionView.value as MergedSubscription
		return normalizeProductId(sub.product_id)
	})

	const getActivePhase = computed<Phase | undefined>(() =>
		Object.values(getPhases.value).find((phase) => phase?.is_active),
	)

	const checkPeriodType = computed(() => {
		const phase = getActivePhase.value
		return {
			isCommitment: phase?.phase_type === 'commitment',
			isTrial: phase?.phase_type === 'trial',
			isRegular: phase?.phase_type === 'regular',
		}
	})

	const checkProvider = computed(() => {
		const provider = (getSubscriptionView.value as MergedSubscription).provider
		return {
			isPalta: provider === 'palta',
			isStripe: provider === 'stripe',
			isBraintree: provider === 'braintree',
			isPrimer: provider === 'primer',
			isFlex: provider === 'flex',
			isPaypal: provider === 'paypal',
		}
	})

	const checkStatus = computed(() => ({
		isActive: Boolean(hasSubscription.value && !subscription.value?.cancelled),
		isPaused: Boolean(subscription.value?.is_paused),
		isCancelled: Boolean(subscription.value?.cancelled),
		// `declined` = prescription rejected → a cancelled-with-refund terminal state. Tracked
		// off the status string (the `cancelled` flag isn't guaranteed for it) so consumers can
		// treat it like a patient cancellation — e.g. hide the /status "Manage subscription" entry.
		isDeclined: subscription.value?.status === 'declined',
		isExpired: Boolean(subscription.value?.is_expired),
		isOverdue: Boolean(subscription.value?.is_overdue),
	}))

	/**
	 * Prescription clinical-review state for the /status "Rx Status" row. Also gates the
	 * "Before you go…" modal and the `/cancel/scope` screen, both of which only make sense while a
	 * clinician is still reviewing.
	 *
	 * Comes straight from the API (`order.rx_status`, translated in `mapRxStatus`) — no placeholder —
	 * but only while the subscription's own `status` is one of `RX_STATUS_STATUSES` (`active` /
	 * `awaiting_approval`). `undefined` otherwise: no subscription, a status outside that pair
	 * (including an absent one — an unknown plan state must not carry a confident Rx claim), no
	 * clinical order, or a backend older than med-billing-engine#204 (still the case on prod). In
	 * every one of those the row is omitted, `SubscriptionDetailsScreenView.rx_status` is not sent,
	 * and the cancel flow keeps its original entry point.
	 */
	const getRxStatus = computed<RxStatus | undefined>(() => {
		if (!hasSubscription.value) return undefined
		if (!RX_STATUS_STATUSES.includes(subscription.value?.status ?? '')) return undefined
		return subscription.value?.rx_status
	})

	/** True while the prescription is awaiting clinical review — see `getRxStatus`. */
	const isRxUnderReview = computed(() => getRxStatus.value === 'in_review')

	/**
	 * The clinician rejected the prescription. The engine declines the subscription for it
	 * (`MarkRejected` → `declined`); an order reading `rejected` on a plan nobody cancelled counts too.
	 * A plan the user cancelled does not: a `hard` cancel under review (or a paid-in-full refund) takes
	 * the clinic order back itself, and a cancelled order reads `rejected` as well (engine `rxStatusMap`).
	 */
	const isRxRejected = computed(() => {
		const sub = subscription.value
		if (!sub) return false
		return sub.status === 'declined' || (sub.rx_status === 'rejected' && !sub.cancelled)
	})

	/**
	 * When access ends after a **soft** cancel — the date `/cancel/confirm` and `/cancel/done` put
	 * in their "ends on {date}" copy.
	 *
	 * `current_period_end_at` for an ordinary plan, but `commitment_end_at` for a commitment: the
	 * backend winds a commitment down to the end of its cycle (`expired_at = commitment_end_at +
	 * renewal grace`), so the next charge date would understate the plan's life by every iteration
	 * still owed. `commitment_end_at` is non-null only on a commitment, which is exactly the
	 * condition, so no phase check is needed here.
	 *
	 * Two things this getter is deliberately *not*:
	 * - **the next charge.** `/status` keeps `current_period_end_at` for its "Charge Date" row.
	 * - **correct after a `hard` cancel**, which sets `expired_at = now`. The "ends on {date}" copy
	 *   has always contradicted `hard` (a known copy gap — see docs/INDEX.md); on a commitment the
	 *   gap is simply wider, because the horizon shown is the cycle end rather than the period end.
	 *   Not papered over here: what a `hard` receipt should say is a copy decision, not a date.
	 */
	const getAccessEndsAt = computed<string | null>(
		() => subscription.value?.commitment_end_at ?? subscription.value?.current_period_end_at ?? null,
	)

	const checkBillingIntervalType = computed(() => {
		const sub = getSubscriptionView.value as MergedSubscription
		return {
			isWeek: sub.regular?.interval_unit === 'week',
			isMonth: sub.regular?.interval_unit === 'month',
			isYear: sub.regular?.interval_unit === 'year',
			isDays: sub.regular?.interval_unit === 'days',
		}
	})

	const checkFundingType = computed(() => {
		const sub = getSubscriptionView.value as MergedSubscription
		return { credit: sub.funding_type === 'CREDIT' }
	})

	const isTokenizationRequired = computed(() => {
		const sub = getSubscriptionView.value as MergedSubscription
		return ['pix_automatico', 'ideal'].includes(sub.payment_method ?? '')
	})

	function isTrialEqual(duration: number, unit: string): boolean {
		const sub = getSubscriptionView.value as MergedSubscription
		return sub.trial?.interval_duration === duration && sub.trial?.interval_unit === unit
	}

	const getIsLegacy = computed(() => checkProvider.value.isStripe || checkProvider.value.isBraintree)

	const getIsModern = computed(
		() =>
			checkProvider.value.isPalta ||
			checkProvider.value.isPrimer ||
			checkProvider.value.isFlex ||
			checkProvider.value.isPaypal,
	)

	const canUpgrade = computed(() => getIsModern.value && !checkPeriodType.value.isRegular)

	const canPause = computed(() => {
		const sub = getSubscriptionView.value as MergedSubscription
		return getIsModern.value && Boolean(sub.is_pause_allowed)
	})

	const canResume = computed(() => {
		const sub = getSubscriptionView.value as MergedSubscription
		return Boolean(sub.is_paused)
	})

	const canPurchaseCancelOffer = computed(() => {
		const sub = getSubscriptionView.value as MergedSubscription
		if (sub.payment_method === 'pix_automatico') return false
		return Boolean(sub.is_cancel_offer_allowed)
	})

	function canUseFeature(feature: 'upgrade' | 'pause' | 'resume' | 'purchaseCancelOffer'): boolean {
		switch (feature) {
			case 'upgrade':
				return canUpgrade.value
			case 'pause':
				return canPause.value
			case 'resume':
				return canResume.value
			case 'purchaseCancelOffer':
				return canPurchaseCancelOffer.value
			default:
				return false
		}
	}

	function setSubscription(next: Subscription | null) {
		if (!next) {
			subscription.value = null
			return
		}
		// Without the downgrade special case, `scheduled` is always treated as a future
		// state that supersedes the current one (matches funnel behavior for non-downgrade
		// scheduled product changes).
		if (next.scheduled) {
			const scheduled = next.scheduled
			if (scheduled.phases?.[0]) scheduled.phases[0].is_active = true
			// `rx_status` is the one field the merge must not clobber: the backend attaches the
			// clinical order to the top-level DTO only, so the scheduled sibling always maps to
			// `undefined` and a blind spread would erase a real Rx state (hiding the row and the
			// pre-Rx cancel branch for anyone with a scheduled subscription).
			// `commitment_end_at` has the same shape of problem: it lives on the top-level DTO, so a
			// blind spread nulls it and `getAccessEndsAt` silently falls back to the next charge date.
			next = {
				...next,
				...scheduled,
				rx_status: scheduled.rx_status ?? next.rx_status,
				commitment_end_at: scheduled.commitment_end_at ?? next.commitment_end_at,
				// Same for the exit options, how it was cancelled and the supply schedule (DEV-35727,
				// DEV-36310): top-level only — the scheduled sibling always maps to "nothing offered".
				cancellation_type: next.cancellation_type,
				cancellation_options: next.cancellation_options,
				supply_schedule: next.supply_schedule,
			}
		}
		subscription.value = next
	}

	function subscriptionMarkCanceled() {
		if (subscription.value) {
			subscription.value = { ...subscription.value, cancelled: true }
		}
	}

	/**
	 * Re-read the subscription in the middle of a flow (the commitment exit screens: the quotes move
	 * with every charge and shipment). Unlike `fetchSubscription` a failure keeps what we have and
	 * stays silent — the last known options are still the best thing to show. Resolves `true` when
	 * the read succeeded.
	 */
	async function refreshSubscription(): Promise<boolean> {
		try {
			const data = await getSubscription()
			if (data) setSubscription(data)
			return Boolean(data)
		} catch {
			return false
		}
	}

	async function fetchSubscription({ includeExpired = false }: FetchSubscriptionParams = {}) {
		loading.value = true
		error.value = null
		try {
			const data = await getSubscription({ includeExpired })
			setSubscription(data)
		} catch (err) {
			error.value = err instanceof Error ? err.message : String(err)
			setSubscription(null)
		} finally {
			loading.value = false
			bootstrapped.value = true
		}
		return getSubscriptionView.value
	}

	/**
	 * Settle the store after a cancel or a pay-fee. Their answers are no read: billing builds them without
	 * the clinic order (`order.rx_status` comes with GET /subscription only — DEV-36626), so a plan under
	 * review would read as approved, with its supplies and counters. Re-read the authoritative state; the
	 * answer stands in only when the GET fails or finds nothing (a 404 once the plan is gone, a 409 with
	 * no body), and with neither we keep what we have — /cancel/done still needs its dates.
	 */
	async function settleAfterWrite(answer: Subscription | null) {
		const next = (await getSubscription().catch(() => null)) ?? answer
		if (next) setSubscription(next)
	}

	/**
	 * Cancel the loaded subscription.
	 *
	 * `cancellationType` defaults to `soft` — keep access to the end of the paid period (or, on a
	 * commitment, to the end of the commitment cycle: the backend derives `soft_commitment` from
	 * `soft` itself) and leave the prescription alone. Both cases match the "ends on {date}" UI,
	 * which reads `getAccessEndsAt`. `/cancel/confirm` passes `hard` when the user picked "cancel my
	 * plan and prescription" on `/cancel/scope` — including on a commitment, where it breaks the
	 * cycle instead of winding it down (deliberate; see ADR 0016). The reasons picked on
	 * `/cancel/reasons` go along as `exit_reason` (`survey.exitReasons`, DEV-36293) — read here, so
	 * no exit call can leave them out; `cancel_reason` / `source` are still set server-side.
	 */
	async function cancel({ cancellationType = 'soft' }: { cancellationType?: CancellationType } = {}) {
		const id = subscription.value?.subscription_id
		if (!id) {
			error.value = 'No subscription to cancel'
			return
		}
		loading.value = true
		error.value = null
		try {
			const updated = await cancelSubscription(id, {
				cancellationType,
				exitReasons: useSurveyStore().exitReasons,
			}).catch(async (err) => {
				// A `hard` refused as "already cancelled": success only if the plan did end `hard` (our own
				// earlier request); over "renewals only" nothing changed, and the caller must say so.
				if (!(err instanceof AlreadyCancelledError)) throw err
				await refreshSubscription()
				if (subscription.value?.cancellation_type !== 'hard') throw err
				return null
			})
			await settleAfterWrite(updated)
			// Reflect cancellation even if the response/re-fetch omitted (or never carried) the flag.
			if (!subscription.value?.cancelled) subscriptionMarkCanceled()
		} catch (err) {
			error.value = err instanceof Error ? err.message : String(err)
			throw err
		} finally {
			loading.value = false
		}
	}

	let payFeeInFlight: Promise<'paid' | 'requires_action'> | null = null

	/**
	 * Pay the commitment exit fee now and end the plan (DEV-35727) — see `payCancellationFee`.
	 *
	 * Resolves `'paid'` once the backend settled the charge (the plan is cancelled server-side) and
	 * `'requires_action'` if the charge ever asks for 3DS — not expected (a recurring charge of the saved
	 * card); the plan is still active then. Throws on a declined card; the
	 * caller routes to `/cancel/commitment/failed`. Carries the survey's reasons like `cancel()`.
	 */
	function payFee(): Promise<'paid' | 'requires_action'> {
		// The endpoint charges on every call (no idempotency key on billing): a second caller while one is in
		// flight — a remounted end-now, the /status buy-out — gets the same request, never a second charge.
		if (!payFeeInFlight) payFeeInFlight = sendPayFee().finally(() => (payFeeInFlight = null))
		return payFeeInFlight
	}

	async function sendPayFee(): Promise<'paid' | 'requires_action'> {
		const id = subscription.value?.subscription_id
		if (!id) {
			error.value = 'No subscription to cancel'
			throw new Error(error.value)
		}
		loading.value = true
		error.value = null
		try {
			const { subscription: updated, requiredAction } = await payCancellationFee(id, {
				exitReasons: useSurveyStore().exitReasons,
			})
			if (requiredAction) return 'requires_action'
			await settleAfterWrite(updated)
			return 'paid'
		} catch (err) {
			error.value = err instanceof Error ? err.message : String(err)
			throw err
		} finally {
			loading.value = false
		}
	}

	return {
		// state
		subscription,
		loading,
		error,
		bootstrapped,
		// getters
		hasSubscription,
		getPhases,
		// `getSubscription` is also the imported API function; expose the view under a
		// distinct name to avoid shadowing in consumers.
		getSubscriptionView,
		getCurrency,
		getOriginalProductId,
		getActivePhase,
		checkPeriodType,
		checkProvider,
		checkStatus,
		getRxStatus,
		isRxUnderReview,
		isRxRejected,
		getAccessEndsAt,
		checkBillingIntervalType,
		checkFundingType,
		isTokenizationRequired,
		getIsLegacy,
		getIsModern,
		canUpgrade,
		canPause,
		canResume,
		canPurchaseCancelOffer,
		// actions / methods
		isTrialEqual,
		canUseFeature,
		setSubscription,
		subscriptionMarkCanceled,
		refreshSubscription,
		fetchSubscription,
		cancel,
		payFee,
	}
})

function normalizeProductId(id: string | undefined | null): string {
	if (typeof id !== 'string' || !id) return ''
	return id.replace(/^(.{9}).*(.{7})$/, '$1$2')
}
